import {
  BaseQueryApi,
  FetchArgs,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import {
  logout,
  setLoggedIn,
  clearCartOnLogout,
  clearWishlist,
} from "State/actions";
import { adminLogout } from "State/Admin/AuthSlice/AdminLoginSlice";
import { RootState } from "State/store";
import { showError } from "Utilities";

// RTK Query endpoint names (the key used in each ApiSlice's `endpoints: (builder) => ({...})`)
// that map to APIs the backend serves without requiring a Bearer JWT.
// Keep this in sync with the backend's public API allowlist.
const PUBLIC_ENDPOINTS = new Set([
  // Authentication
  "loginWithPassword",
  "loginWithOTP",
  "preRegister",
  "register",
  "preOtpLogin",
  "adminLogin",
  // OTP
  "sendTwilioOtp",
  "registerSendTwilioOtp",
  "sendFast2SmsOtp",
  "registerSendFast2SmsOtp",
  "validateFast2SmsOtp",
  // Forgot Password
  "forgotPasswordEmailOtp",
  "resetPasswordVerifyOtp",
  "resetPassword",
  // Captcha
  "getCaptcha",
  // Product Browsing
  "getCategoriesByProductType",
  "getListOfSubCategories",
  "getListOfBookProductsWithFilter",
  "getAllActiveAuthors",
  "getActiveAuthorsByProductType",
  "getAuthorsByCategory",
  "getAllActiveLanguages",
  "getActiveLanguagesByProductType",
  "getAllActiveAcademics",
  "getAllActiveMerchandises",
  "getListOfBanners",
  "getProductTypesAll",
  "getPublicEcomConfig",
  "getBulkPromotions",
  // Product
  "getEcomProductDetailById",
  "getAvailableQtyList",
  "getRecordAvailableQtyList",
  // Search Analytics
  "saveSearchAnalytics",
  "saveNoResultSearchAnalytics",
  // Support email (public contact form)
  "sendSupportEmail",
  // Reviews & Ratings (client/public read; admin read/update/reply require ADMIN JWT - see below)
  "getProductReviewsClient",
  "getAllProductRatings",
  // Events & Content - only the read endpoints that are also used by public storefront pages;
  // create/update/delete require ADMIN JWT (see ADMIN_TOKEN_ENDPOINTS)
  "getAllEvents",
  "getEventById",
  "getAllHistoryCover",
  "getAllHistory",
  "getAllGlossary",
  "getGlossaryById", // shared with Admin/AdminGlossaryEdit.tsx, but also used by the public GlossaryDetail.tsx page
  "getActiveSocialMediaLinks",
  // NOTE: "getAllCartWithId" was previously listed here, but it's only ever called from
  // useCartSync.ts while the user IS logged in (it fetches live product/stock data for the
  // signed-in user's cart items) - excluding it caused 401s. It now correctly sends the token.
  "getTopSellers",
]);

// RTK Query endpoint names that must authenticate with the ADMIN JWT (State/Admin/AuthSlice/AdminLoginSlice)
// instead of the storefront CLIENT JWT. This is every endpoint backing an Admin-panel-only feature -
// confirmed by the backend team: "in all the admin APIs ... pass admin jwt not client jwt".
// Endpoint names are the RTK Query keys, not URLs - cross-reference the source file if in doubt.
const ADMIN_TOKEN_ENDPOINTS = new Set([
  // features/admin/admin.api.ts (loyalty-config + admin subclass CRUD)
  "getAllAdminSubClasses",
  "getAdminSubClassById",
  "saveSubClass",
  "saveLoyaltyConfig",
  "getLoyaltyConfig",
  // Services/Admin/AcademicApiSlice.ts
  "getAllAcademics",
  "getAcademicById",
  "addAcademic",
  "updateAcademic",
  "deleteAcademic",
  "bulkDeleteAcademics",
  // Services/Admin/AdminPaymentApiSlice.ts
  "getPaymentConfig",
  "savePaymentConfig",
  "updatePaymentConfig",
  // Services/Admin/AdminSellBooksApiSlice.ts
  "getAllSellBooks",
  "getSellBookById",
  "updateSellBookStatus",
  // Services/Admin/AuthorApiSlice.ts (admin author master CRUD - distinct from the public active-authors list)
  "getAllAuthors",
  "getAuthorById",
  "addAuthor",
  "updateAuthor",
  "deleteAuthor",
  "bulkDeleteAuthors",
  // Services/Admin/BannerApiSlice.ts (admin banner CRUD - distinct from the public getListOfBanners)
  "getAdminBanners",
  "getAllBannersWithoutBytes",
  "getBannerById",
  "createBanner",
  "updateBanner",
  "deleteBanner",
  "uploadBannerImage",
  "uploadBanner2Image",
  "deleteBannerImage",
  "deleteBanner2Image",
  "bulkDeleteBanners",
  "getMaxBannerLnNo",
  "updateBannerOrder",
  // Services/Admin/BrandApiSlice.ts
  "getAllBrands",
  "getBrandById",
  "addBrand",
  "updateBrand",
  "deleteBrand",
  // Services/Admin/BrandPromotionApiSlice.ts
  "addBrandPromotion",
  "updateBrandPromotion",
  "deleteBrandPromotion",
  "getBrandPromotionById",
  "getAllBrandPromotions",
  // Services/Admin/BulkPromotionApiSlice.ts (getBulkPromotions list stays PUBLIC - see above)
  "getBulkPromotionById",
  "createBulkPromotion",
  "updateBulkPromotion",
  "disableBulkPromotion",
  "deleteBulkPromotion",
  "bulkDeleteBulkPromotions",
  "updateBulkPromotionUsage",
  // Services/Admin/CategoryAdminApiSlice.ts
  "getAllCategories",
  "getAdminCategoriesList",
  "getCategoryById",
  "addCategory",
  "updateCategory",
  "deleteCategory",
  "uploadCategoryImage",
  "deleteCategoryImage",
  "bulkDeleteCategories",
  // Services/Admin/CategoryPromotionApiSlice.ts
  "addCategoryPromotion",
  "updateCategoryPromotion",
  "deleteCategoryPromotion",
  "getCategoryPromotionById",
  "getAllCategoryPromotions",
  // Services/Admin/ChangePasswordApiSlice.ts (admin user-settings change password)
  "changePassword",
  // Services/Admin/CollarApiSlice.ts
  "getAllCollars",
  "getCollarById",
  "addCollar",
  "updateCollar",
  "deleteCollar",
  // Services/Admin/ColorApiSlice.ts
  "getAllColors",
  "getColorById",
  "addColor",
  "updateColor",
  "deleteColor",
  // Services/Admin/ConfigurationApiSlice.ts (/admin/ecom-config)
  "getConfigurationByPlant",
  "updateConfiguration",
  // Services/Admin/DashboardApiSlice.ts (/Dashboard/**)
  "getTotalRevenue",
  "getTotalOrders",
  "getUniqueVisits",
  "getNewUser",
  "getExistingUser",
  "getOrdersOverTime",
  "getLast7DaysSales",
  "getAllTopSelling",
  "getRecentTransactions",
  "getSalesCountByOrderStatus",
  "getSalesValueByOrderStatus",
  "getTodayOrderStatusSummary",
  "getTodayOrderStatusDetails",
  "getDashboardCategories",
  "getDashboardProducts",
  "getDashboardWarehouses",
  "getChannelPerformance",
  "getOrdersByChannel",
  "getSalesByRegion",
  "getSalesSummary",
  "getWarehouseStock",
  "getInventorySummary",
  "getSalesOverview",
  "getSearchAnalyticsLogs",
  "getNoResultSearchAnalyticsLogs",
  // Services/Admin/EcommerceDiscountApiSlice.ts (/admin/ecommerce-discount)
  // NOTE: "getAllOverallPromotions" is NOT here - it's a dual-use read (Admin panel AND the
  // storefront payment page / mini-cart price breakdown). See DUAL_TOKEN_ENDPOINTS below.
  "getOverallPromotionById",
  "getDiscountsByPriceSlab",
  "createOverallPromotion",
  "updateOverallPromotion",
  "deleteOverallPromotion",
  // Services/Admin/FabricApiSlice.ts
  "getAllFabrics",
  "getFabricById",
  "addFabric",
  "updateFabric",
  "deleteFabric",
  // Services/Admin/ItemGroupApi.ts
  "getAllItemGroups",
  // Services/Admin/LanguageApiSlice.ts (admin language master CRUD - distinct from the public active-language list)
  "getAllLanguages",
  "getLanguageById",
  "addLanguage",
  "updateLanguage",
  "deleteLanguage",
  "bulkDeleteLanguages",
  // Services/Admin/LocTypeApiSlice.ts
  "getAllLocTypes",
  // Services/Admin/MerchandiseApiSlice.ts (admin merchandise master CRUD)
  "getAllMerchandises",
  "getMerchandiseById",
  "addMerchandise",
  "updateMerchandise",
  "deleteMerchandise",
  "bulkDeleteMerchandises",
  // Services/Admin/ModelApiSlice.ts
  "getAllModels",
  "getModelById",
  "addModel",
  "updateModel",
  "deleteModel",
  // Services/Admin/OccasionApiSlice.ts
  "getAllOccasions",
  "getOccasionById",
  "addOccasion",
  "updateOccasion",
  "deleteOccasion",
  // Services/Admin/PatternApiSlice.ts
  "getAllPatterns",
  "getPatternById",
  "addPattern",
  "updatePattern",
  "deletePattern",
  // Services/Admin/ProductAdminApiSlice.ts (/admin/products - getProductTypesAll stays PUBLIC)
  "getAllAdminProducts",
  "getAdminProductById",
  "createAdminProduct",
  "updateAdminProduct",
  "deleteAdminProduct",
  "uploadAdminProductMainImage",
  "uploadAdminProductOtherImages",
  "deleteAdminProductMainImage",
  "deleteAdminProductOtherImage",
  "deleteAdminProductsBulk",
  // Services/Admin/ProductFilterApiSlice.ts
  "getAllPrdCollarMst",
  "getAllPrdColorMst",
  "getAllPrdFabricMst",
  "getAllPrdOccasionMst",
  "getAllPrdPatternMst",
  "getAllPrdSizeMst",
  "getAllPrdSleeveMst",
  // Services/Admin/ProductGroupAdminApiSlice.ts (ItemGroups)
  "createItemGroup",
  "getItemGroupById",
  "getItemGroupByName",
  "updateItemGroup",
  "deleteItemGroup",
  "deleteItemGroupsBulk",
  // Services/Admin/ProductPromotionApiSlice.ts
  "addProductPromotion",
  "updateProductPromotion",
  "deleteProductPromotion",
  "getProductPromotionById",
  "getAllProductPromotions",
  // Services/Admin/ProductReviewAdminApiSlice.ts (Admin Review APIs)
  "getProductReviewsAdmin",
  "updateProductReview",
  "replyToReview",
  "updateReply",
  // Services/Admin/SectionApiSlice.ts
  "getAllSections",
  "getSectionById",
  "createSection",
  "updateSection",
  "deleteSection",
  "uploadSectionImages",
  // Services/Admin/SecurityConfigApiSlice.ts
  "getSecurityConfig",
  "saveOrUpdateSecurityConfig",
  // Services/Admin/ShipmentConfigApiSlice.ts (/admin/pickup-location)
  // NOTE: "getPickupLocations" is NOT here - it's a dual-use read (Admin panel AND the
  // storefront payment page, which needs the origin pincode for shipping calc). See
  // DUAL_TOKEN_ENDPOINTS below.
  "savePickupLocation",
  // Services/Admin/SizeApiSlice.ts
  "getAllSizes",
  "getSizeById",
  "addSize",
  "updateSize",
  "deleteSize",
  // Services/Admin/SleeveApiSlice.ts
  "getAllSleeves",
  "getSleeveById",
  "addSleeve",
  "updateSleeve",
  "deleteSleeve",
  // Services/Admin/SocialMediaApiSlice.ts (admin CRUD - distinct from the public getActiveSocialMediaLinks)
  "getAllSocialMediaLinks",
  "getSocialMediaLinkByAccountType",
  "createSocialMediaLink",
  "updateSocialMediaLink",
  "deleteSocialMediaLink",
  // Services/Admin/SubCategoryApiSlice.ts
  "getAllSubCategories",
  "getSubCategoryById",
  "addSubCategory",
  "updateSubCategory",
  "deleteSubCategory",
  // Services/Admin/SubClassApiSlice.ts
  "getAdminSubClasses",
  "getAdminSubClassesList",
  "getSubClassById",
  "createSubClass",
  "updateSubClass",
  "deleteSubClass",
  "bulkDeleteSubClasses",
  // Services/EventApiSlice.ts admin mutations (getAllEvents/getEventById stay PUBLIC)
  "createEvent",
  "updateEvent",
  "deleteEvent",
  "deleteEventImage",
  "uploadEventImage",
  "bulkDeleteEvents",
  // Services/EventRegistrationApiSlice.ts admin management (createEventRegistration stays CLIENT)
  "getAllEventRegistrations",
  "getEventRegistrationById",
  "updateEventRegistration",
  "deleteEventRegistration",
  // Services/GlossaryApiSlice.ts admin mutations (getAllGlossary/getGlossaryById stay PUBLIC)
  "createGlossary",
  "updateGlossary",
  "deleteGlossary",
  "bulkDeleteGlossary",
  "deleteGlossaryImage",
  "uploadGlossaryImage",
  // Services/HistoryCoverApiSlice.ts admin mutations (getAllHistoryCover stays PUBLIC)
  "saveHistoryCover",
  "updateHistoryCover",
  "deleteHistoryCover",
  "deleteHistoryCoverImage",
  // Services/HistoryApiSlice.ts admin (getAllHistory stays PUBLIC; getHistoryById is admin-only, unlike glossary's)
  "getHistoryById",
  "createHistory",
  "updateHistory",
  "deleteHistory",
  "deleteHistoryImage",
  "bulkDeleteHistory",
]);

// RTK Query endpoint names that are served off an /admin/** route but are ALSO consumed by the
// public storefront (e.g. the payment page reads overall discounts + the pickup-location origin
// pincode to compute the price breakdown and shipping). The backend accepts either JWT for these.
// A shopper on the checkout page has a CLIENT token; an admin viewing the same data in the panel
// has an admin token. These are storefront-facing endpoints, so prefer the CLIENT token and only
// fall back to the admin token when there is no client session (i.e. the Admin panel pages that
// also read this data). This matters when an admin is ALSO signed into the storefront in the same
// browser - both tokens live in the same store, and sending the admin token for a shopper's
// checkout request is wrong. Never force a logout on a 401 here (a stale admin session must not
// sign the shopper out, and vice-versa).
const DUAL_TOKEN_ENDPOINTS = new Set([
  "getAllOverallPromotions",
  "getPickupLocations",
]);

const baseQuery = fetchBaseQuery({
  // Mobile Testing
  // // baseUrl: 'http://192.168.1.39:9071/api/',

  //  baseUrl: 'http://localhost:9071/api/',

  // textilestore, if REACT_APP_PLANT=C2...
  // mooremarket.in - PRODUCTION
  baseUrl: "https://api-fmworker-ind.u-clo.com/AlphabitEcommerce.0.1/api/",

  // caviaarmode, if REACT_APP_PLANT=C7...
  // mooremarket.u-clo.com - TESTING
  // baseUrl: "https://api-ind-ecommerce.u-clo.com/AlphabitEcommerce.0.1/api/",

  credentials: "include",
  prepareHeaders: (headers, { getState, endpoint }) => {
    const state = getState() as RootState;
    // Admin panel auth lives in a separate slice (State/Admin/AuthSlice/AdminLoginSlice) with its
    // own token, distinct from the client storefront's `login.token`. Every Admin-panel-only
    // endpoint is listed in ADMIN_TOKEN_ENDPOINTS and gets the admin token; everything else
    // (storefront/client-facing endpoints) uses the client token.
    const token = DUAL_TOKEN_ENDPOINTS.has(endpoint)
      ? state.login.token || state.adminlogin.adminToken
      : ADMIN_TOKEN_ENDPOINTS.has(endpoint)
      ? state.adminlogin.adminToken
      : state.login.token;
    if (token && !PUBLIC_ENDPOINTS.has(endpoint)) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// Guards against a reauth storm: resetApiState() below forces every mounted query to
// refetch immediately. If the session is genuinely stale, those refetches 401 again,
// each one re-triggering resetApiState() - an infinite loop of requests that never
// settle (seen as endless "pending" rows in the network panel, page stuck loading).
// Cooldown ensures only one reset happens per session type within REAUTH_COOLDOWN_MS.
const REAUTH_COOLDOWN_MS = 3000;
let lastClientReauthAt = 0;
let lastAdminReauthAt = 0;

const baseQueryWithReAuth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: {}
) => {
  const result = await baseQuery(args, api, extraOptions);

  // A 401 on a public endpoint doesn't mean the session is invalid (no token was sent),
  // so only force a logout when the endpoint actually required authentication.
  if (
    result.error?.status === 401 &&
    !PUBLIC_ENDPOINTS.has(api.endpoint) &&
    !DUAL_TOKEN_ENDPOINTS.has(api.endpoint)
  ) {
    const now = Date.now();
    const isAdminEndpoint = ADMIN_TOKEN_ENDPOINTS.has(api.endpoint);
    const lastReauthAt = isAdminEndpoint ? lastAdminReauthAt : lastClientReauthAt;

    if (now - lastReauthAt > REAUTH_COOLDOWN_MS) {
      const loginPath = isAdminEndpoint ? "/admin-login" : "/login";

      if (isAdminEndpoint) {
        lastAdminReauthAt = now;
        // Admin panel session lives in a separate slice from the storefront session -
        // an expired/invalid admin JWT must not touch the client's login state.
        api.dispatch(adminLogout());
      } else {
        lastClientReauthAt = now;
        api.dispatch(logout());
        api.dispatch(setLoggedIn(false));
        api.dispatch(clearCartOnLogout());
        api.dispatch(clearWishlist());
      }
      // Without this, previously cached query results (e.g. pickup locations fetched
      // while authenticated) keep being served from the RTK Query cache after the
      // token is cleared, since this path doesn't trigger a page reload.
      api.dispatch(ApiService.util.resetApiState());

      showError("Authentication failed. Please login to continue.");

      // baseQuery runs outside the React tree (no access to react-router's navigate),
      // and the storefront has no route guard to react to the cleared auth state on
      // its own (unlike AdminRouteGuard) - so force the redirect directly here.
      if (window.location.pathname !== loginPath) {
        window.location.href = loginPath;
      }
    }
  }

  return result;
};

export const ApiService = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReAuth,
  tagTypes: [
    "Banner",
    "Section",
    "Dashboard",
    "PrdProduct",
    "PrdProductGroup",
    "PrdSubCategory",
    "PrdCategory",
    "PrdBrand",
    "PrdModel",
    "PrdClass",
    "PrdType",
    "Collars",
    "PrdColor",
    "PrdFabric",
    "PrdOccasion",
    "PrdPattern",
    "PrdSize",
    "PrdSleeve",
    "Configuration",
    "Payment",
    "SecurityConfig",
    "ItemGroup",
    "BrandPromotion",
    "CategoryPromotion",
    "ProductPromotion",
    "LocType",
    "Author",
    "Academic",
    "Language",
    "Merchandise",
    "SubClass",
    "Wishlist",
    "Event",
    "EventRegistration",
    "Glossary",
    "History",
    "HistoryCover",
    "SocialMedia",
    "ProductReviews",
    "SocialMedia",
    "ChangePassword",
    "LoyaltyConfig",
    "BulkPromotion",
    "AdminProducts",
    "EcommerceDiscount",
    "Addresses",
    "Profile",
  ],
  endpoints: (builder) => ({}),
});

export { baseQueryWithReAuth };

